import { cartLineKey, getProductStockAvailable } from "./productSizes";
import { getSizeFieldsForCategory } from "../../../dashboard/merchant/utils/categoryFormConfig";

export const getStockAvailable = (item) => {
  if (item?.stockAvailable != null) {
    return Math.max(0, parseInt(item.stockAvailable, 10) || 0);
  }
  const sizeFields = getSizeFieldsForCategory(item?.category);
  return getProductStockAvailable(item, item?.selectedSize ?? null, sizeFields);
};

export const getCartLineQuantity = (cartItems, productToAdd) => {
  const lineKey = cartLineKey({
    _id: productToAdd._id,
    selectedSize: productToAdd.selectedSize ?? null,
  });
  const existing = cartItems.find((item) => cartLineKey(item) === lineKey);
  return existing?.quantity || 0;
};

export const validateAddToCart = (cartItems, productToAdd, quantityToAdd = 1) => {
  const name = productToAdd.name || "This product";
  const maxStock = getStockAvailable(productToAdd);
  const inCart = getCartLineQuantity(cartItems, productToAdd);
  const qty = Math.max(1, parseInt(quantityToAdd, 10) || 1);
  const newTotal = inCart + qty;

  if (maxStock <= 0) {
    return { ok: false, message: `${name} is out of stock.` };
  }

  if (newTotal > maxStock) {
    if (inCart >= maxStock) {
      return {
        ok: false,
        message: `Maximum available (${maxStock}) already in your cart for ${name}.`,
      };
    }
    const remaining = maxStock - inCart;
    return {
      ok: false,
      message: `Only ${remaining} more can be added (${maxStock} in stock for ${name}).`,
    };
  }

  return { ok: true };
};

export const buildCartLineItem = (product, cartQuantity, selectedSize = null) => ({
  _id: product._id,
  name: product.name,
  price: product.price,
  images: product.images,
  description: product.description,
  category: product.category,
  stockAvailable: getProductStockAvailable(product, selectedSize ?? product.selectedSize ?? null),
  quantity: cartQuantity,
  selectedSize: selectedSize ?? product.selectedSize ?? null,
  sizeSmall: product.sizeSmall,
  sizeMedium: product.sizeMedium,
  sizeLarge: product.sizeLarge,
  sizeSmallextraLarge: product.sizeSmallextraLarge,
  sizeXXLarge: product.sizeXXLarge,
  sizeXXXLarge: product.sizeXXXLarge,
});

export const reconcileCartWithLiveStock = (cartItems, stockUpdates = [], issues = []) => {
  const stockMap = new Map(
    stockUpdates.map((entry) => [String(entry.productId), entry])
  );

  const messages = [];
  const updatedItems = [];

  for (const item of cartItems) {
    const productId = String(item._id);
    const live = stockMap.get(productId);

    if (!live) {
      messages.push(
        `"${item.name}" is no longer sold on the marketplace and was removed from your cart.`
      );
      continue;
    }

    const available = Math.max(0, parseInt(live.available, 10) || 0);

    if (available <= 0) {
      messages.push(
        `"${item.name}" is sold out (0 in stock). It was removed from your cart.`
      );
      continue;
    }

    if (item.quantity > available) {
      messages.push(
        `"${item.name}": only ${available} left — quantity changed from ${item.quantity} to ${available}.`
      );
      updatedItems.push({
        ...item,
        quantity: available,
        stockAvailable: available,
      });
      continue;
    }

    updatedItems.push({
      ...item,
      stockAvailable: available,
    });
  }

  const readyItems = updatedItems.filter((item) => item.quantity > 0);

  return {
    items: readyItems,
    hadChanges: messages.length > 0 || readyItems.length !== cartItems.length,
    messages,
    removedCount: cartItems.length - readyItems.length,
    hasBlockingIssues: issues.some(
      (issue) => issue.status === "out_of_stock" || issue.status === "not_found"
    ),
  };
};

/** Items safe to send to checkout / cart sync (quantity >= 1, in stock). */
export const getCheckoutReadyItems = (items = []) =>
  items.filter(
    (item) =>
      item.quantity > 0 &&
      (item.stockAvailable == null || item.stockAvailable > 0)
  );
